import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchPartnerDealerAccounts,
  reorderPartnerDealerPriority,
  startPartnerDealerPortalSession,
  updatePartnerDealer,
} from "../../services/automapPartnerDealerApi";
import { rememberPartnerDealerPortalTarget } from "../../constants/automapPartnerDealerPortal";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import "../../styles/partnerDealers.css";

const SORT_OPTIONS = [
  { value: "search_priority", label: "Search priority" },
  { value: "dealer_name", label: "Name (A–Z)" },
  { value: "listings", label: "Most listings" },
  { value: "locations", label: "Most locations" },
  { value: "sources", label: "Most sources" },
];

const CRM_FILTERS = [
  { value: "all", label: "All accounts" },
  { value: "crm", label: "CRM saved" },
  { value: "needs_crm", label: "Needs CRM" },
];

const VEHICLE_FILTERS = [
  { value: "all", label: "Any inventory" },
  { value: "new", label: "Sells new" },
  { value: "used", label: "Sells used" },
  { value: "multi_location", label: "Multi-location" },
];

function accountInitials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name).slice(0, 2).toUpperCase() || "DA";
}

function VehicleSalesBadges({ vehicleSales, compact = false }) {
  if (!vehicleSales?.new && !vehicleSales?.used && !vehicleSales?.certifiedPreowned) {
    return <span className="dealer-hub-card__muted">Unknown</span>;
  }

  return (
    <div className={`dealer-hub-card__pills${compact ? " dealer-hub-card__pills--compact" : ""}`}>
      {vehicleSales.new ? <span className="partner-dealers-badge partner-dealers-badge--new">New</span> : null}
      {vehicleSales.used ? (
        <span className="partner-dealers-badge partner-dealers-badge--used">Used</span>
      ) : null}
      {vehicleSales.certifiedPreowned ? (
        <span className="partner-dealers-badge partner-dealers-badge--cpo">CPO</span>
      ) : null}
    </div>
  );
}

function AccountStatusBadge({ account }) {
  if (account.needsCrmAccount) {
    return <span className="dealer-hub-card__badge dealer-hub-card__badge--warning">Needs CRM</span>;
  }
  if (account.hasCrmRecord) {
    return <span className="dealer-hub-card__badge dealer-hub-card__badge--crm">CRM</span>;
  }
  return <span className="dealer-hub-card__badge dealer-hub-card__badge--registry">Registry</span>;
}

function DealerAccountCard({
  account,
  path,
  portalOpeningId,
  onOpenPortal,
  onPriorityChange,
  onPriorityInput,
}) {
  const [expanded, setExpanded] = useState(false);
  const dealerId = account.primaryDealerId;
  const accountRouteId = account.crmDealerId || account.id;
  const crmDealerId = account.crmDealerId || (account.hasCrmRecord ? account.id : null);
  const cityPreview = [...new Set((account.locations || []).map((l) => l.city).filter(Boolean))].slice(
    0,
    4
  );
  const sourcePreview = (account.dataSources || []).slice(0, 4);

  return (
    <article className="dealer-hub-card">
      <div className="dealer-hub-card__top">
        <div className="dealer-hub-card__identity">
          <span className="dealer-hub-card__avatar" aria-hidden="true">
            {accountInitials(account.businessName)}
          </span>
          <div className="dealer-hub-card__title-wrap">
            <h3 className="dealer-hub-card__title">
              <Link to={path(`dealers/${encodeURIComponent(accountRouteId)}`)}>{account.businessName}</Link>
            </h3>
            {account.dealerGroupName && account.dealerGroupName !== account.businessName ? (
              <p className="dealer-hub-card__subtitle">{account.dealerGroupName}</p>
            ) : null}
          </div>
        </div>
        <div className="dealer-hub-card__badges">
          <AccountStatusBadge account={account} />
          <span className={`dealer-hub-card__badge dealer-hub-card__badge--status dealer-hub-card__badge--${account.status}`}>
            {account.status}
          </span>
        </div>
      </div>

      <div className="dealer-hub-card__metrics">
        <div className="dealer-hub-card__metric">
          <span className="dealer-hub-card__metric-value">{account.locationCount}</span>
          <span className="dealer-hub-card__metric-label">Locations</span>
        </div>
        <div className="dealer-hub-card__metric">
          <span className="dealer-hub-card__metric-value">{account.dataSourceCount}</span>
          <span className="dealer-hub-card__metric-label">Sources</span>
        </div>
        <div className="dealer-hub-card__metric">
          <span className="dealer-hub-card__metric-value">{account.profiles?.length || 0}</span>
          <span className="dealer-hub-card__metric-label">Profiles</span>
        </div>
        <div className="dealer-hub-card__metric dealer-hub-card__metric--highlight">
          <span className="dealer-hub-card__metric-value">{account.activeListingCount}</span>
          <span className="dealer-hub-card__metric-label">Listings</span>
        </div>
      </div>

      <div className="dealer-hub-card__row">
        <span className="dealer-hub-card__row-label">Vehicle sales</span>
        <VehicleSalesBadges vehicleSales={account.vehicleSales} compact />
      </div>

      {cityPreview.length ? (
        <div className="dealer-hub-card__chips-block">
          <span className="dealer-hub-card__row-label">Cities</span>
          <div className="dealer-hub-card__chips">
            {cityPreview.map((city) => (
              <span key={city} className="dealer-hub-card__chip">
                {city}
              </span>
            ))}
            {(account.locations?.length || 0) > cityPreview.length ? (
              <span className="dealer-hub-card__chip dealer-hub-card__chip--more">
                +{(account.locations?.length || 0) - cityPreview.length} more
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {sourcePreview.length ? (
        <div className="dealer-hub-card__chips-block">
          <span className="dealer-hub-card__row-label">Top sources</span>
          <div className="dealer-hub-card__chips">
            {sourcePreview.map((source) => (
              <span key={source.id} className="dealer-hub-card__chip" title={source.typeLabel || source.type}>
                {source.label}
              </span>
            ))}
            {(account.dataSources?.length || 0) > sourcePreview.length ? (
              <span className="dealer-hub-card__chip dealer-hub-card__chip--more">
                +{(account.dataSources?.length || 0) - sourcePreview.length} more
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {crmDealerId ? (
        <div className="dealer-hub-card__priority">
          <label className="dealer-hub-card__row-label" htmlFor={`priority-${account.id}`}>
            Search priority
          </label>
          <input
            id={`priority-${account.id}`}
            type="number"
            min="0"
            max="10000"
            className="dealer-hub-card__priority-input"
            value={account.searchPriorityScore ?? 0}
            onChange={(event) => onPriorityInput(account.id, event.target.value)}
            onBlur={(event) => onPriorityChange(crmDealerId, event.target.value)}
          />
        </div>
      ) : null}

      <div className="dealer-hub-card__footer">
        <Link className="dealer-hub-card__btn dealer-hub-card__btn--primary" to={path(`dealers/${dealerId}/inventory`)}>
          Inventory
        </Link>
        {crmDealerId ? (
          <Link className="dealer-hub-card__btn" to={path(`dealers/${crmDealerId}/account`)}>
            CRM
          </Link>
        ) : null}
        <button
          type="button"
          className="dealer-hub-card__btn"
          disabled={!crmDealerId || portalOpeningId === crmDealerId}
          onClick={() => onOpenPortal(crmDealerId || dealerId)}
        >
          {portalOpeningId === (crmDealerId || dealerId) ? "Opening…" : "View as dealer"}
        </button>
        <button
          type="button"
          className="dealer-hub-card__btn dealer-hub-card__btn--ghost"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Hide details" : "Details"}
        </button>
      </div>

      {expanded ? (
        <div className="dealer-hub-card__details">
          <DetailTable
            title={`Locations (${account.locationCount})`}
            empty="No locations recorded yet."
            headers={["Location", "City", "Profile", "Source"]}
            rows={(account.locations || []).map((location) => [
              <>
                {location.name}
                {location.isPrimary ? (
                  <span className="dealer-hub-card__inline-badge">Primary</span>
                ) : null}
              </>,
              <>
                {location.city || "—"}
                {location.neighborhood ? `, ${location.neighborhood}` : ""}
              </>,
              location.profileId ? (
                <Link to={path(`dealers/${location.profileId}/inventory`)}>
                  {location.profileName || "Registry profile"}
                </Link>
              ) : (
                <span className="dealer-hub-card__muted">CRM only</span>
              ),
              location.source === "registry" ? "Registry" : "CRM",
            ])}
          />

          <DetailTable
            title={`Data sources (${account.dataSourceCount})`}
            empty="No ingested data sources linked yet."
            headers={["Source", "Type", "Profile", "Seen"]}
            rows={(account.dataSources || []).map((source) => [
              source.url ? (
                <a href={source.url} target="_blank" rel="noreferrer">
                  {source.label}
                </a>
              ) : (
                source.label
              ),
              source.typeLabel || source.type,
              source.profileId ? (
                <Link to={path(`dealers/${source.profileId}/inventory`)}>
                  {source.profileName || "Registry profile"}
                </Link>
              ) : (
                "—"
              ),
              source.listingCount ?? 0,
            ])}
          />

          <DetailTable
            title={`Registry profiles (${account.profiles?.length || 0})`}
            empty="No linked registry profiles."
            headers={["Profile", "City", "Branch", "Listings"]}
            rows={(account.profiles || []).map((profile) => [
              <Link to={path(`dealers/${profile.id}/inventory`)}>{profile.displayName}</Link>,
              profile.city || "—",
              profile.isBranch ? profile.branchName || "Yes" : "—",
              profile.activeListingCount ?? 0,
            ])}
          />
        </div>
      ) : null}
    </article>
  );
}

function DetailTable({ title, headers, rows, empty }) {
  return (
    <section className="dealer-hub-card__detail-section">
      <h4>{title}</h4>
      {rows.length ? (
        <div className="dealer-hub-card__table-wrap">
          <table className="dealer-hub-card__table">
            <thead>
              <tr>
                {headers.map((header) => (
                  <th key={header} scope="col">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((cells, rowIndex) => (
                <tr key={rowIndex}>
                  {cells.map((cell, cellIndex) => (
                    <td key={cellIndex}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="dealer-hub-card__muted">{empty}</p>
      )}
    </section>
  );
}

function sortAccounts(accounts, sort) {
  const items = [...accounts];
  switch (sort) {
    case "dealer_name":
      return items.sort((a, b) => (a.businessName || "").localeCompare(b.businessName || ""));
    case "listings":
      return items.sort((a, b) => (b.activeListingCount ?? 0) - (a.activeListingCount ?? 0));
    case "locations":
      return items.sort((a, b) => (b.locationCount ?? 0) - (a.locationCount ?? 0));
    case "sources":
      return items.sort((a, b) => (b.dataSourceCount ?? 0) - (a.dataSourceCount ?? 0));
    case "search_priority":
    default:
      return items.sort((a, b) => {
        const crmFirst = Number(b.hasCrmRecord) - Number(a.hasCrmRecord);
        if (crmFirst !== 0) return crmFirst;
        const scoreDelta = (b.searchPriorityScore ?? 0) - (a.searchPriorityScore ?? 0);
        if (scoreDelta !== 0) return scoreDelta;
        return (b.activeListingCount ?? 0) - (a.activeListingCount ?? 0);
      });
  }
}

function filterAccounts(accounts, { search, crmFilter, vehicleFilter }) {
  let items = [...accounts];
  const q = search.trim().toLowerCase();

  if (q) {
    items = items.filter((account) => {
      const haystack = [
        account.businessName,
        account.dealerGroupName,
        account.status,
        ...(account.locations || []).map((location) => `${location.city} ${location.name}`),
        ...(account.profiles || []).map((profile) => `${profile.displayName} ${profile.city}`),
        ...(account.dataSources || []).map((source) => `${source.label} ${source.profileName}`),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }

  if (crmFilter === "crm") {
    items = items.filter((account) => account.hasCrmRecord);
  } else if (crmFilter === "needs_crm") {
    items = items.filter((account) => account.needsCrmAccount);
  }

  if (vehicleFilter === "new") {
    items = items.filter((account) => account.vehicleSales?.new);
  } else if (vehicleFilter === "used") {
    items = items.filter((account) => account.vehicleSales?.used);
  } else if (vehicleFilter === "multi_location") {
    items = items.filter((account) => (account.locationCount ?? 0) >= 2);
  }

  return items;
}

export default function PartnerDealersPage() {
  const { path, portalLabel } = usePartnerPortalBase();
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingOrder, setSavingOrder] = useState(false);
  const [portalOpeningId, setPortalOpeningId] = useState("");
  const [search, setSearch] = useState("");
  const [crmFilter, setCrmFilter] = useState("all");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [sort, setSort] = useState("search_priority");

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await fetchPartnerDealerAccounts({ sort: "search_priority" });
      setAccounts(result.accounts || []);
      setSummary(result.summary || null);
    } catch (err) {
      setError(err.message || "Unable to load dealer accounts.");
      setAccounts([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const filteredAccounts = useMemo(
    () => sortAccounts(filterAccounts(accounts, { search, crmFilter, vehicleFilter }), sort),
    [accounts, search, crmFilter, vehicleFilter, sort]
  );

  const crmAccounts = useMemo(
    () => accounts.filter((account) => account.hasCrmRecord),
    [accounts]
  );

  const dashboardStats = useMemo(() => {
    const base = summary || {};
    return [
      { label: "Dealer accounts", value: base.accountCount ?? accounts.length, hint: "Grouped businesses" },
      { label: "CRM saved", value: base.crmAccountCount ?? crmAccounts.length, hint: "Ready for operations" },
      { label: "Locations", value: base.locationCount ?? 0, hint: "Branches & showrooms" },
      { label: "Data sources", value: base.dataSourceCount ?? 0, hint: "Feeds & marketplaces" },
      { label: "Active listings", value: base.activeListingCount ?? 0, hint: "Live inventory" },
    ];
  }, [summary, accounts.length, crmAccounts.length]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (crmFilter !== "all") count += 1;
    if (vehicleFilter !== "all") count += 1;
    if (search.trim()) count += 1;
    return count;
  }, [crmFilter, vehicleFilter, search]);

  const clearFilters = () => {
    setSearch("");
    setCrmFilter("all");
    setVehicleFilter("all");
    setSort("search_priority");
  };

  const handleSaveOrder = async () => {
    try {
      setSavingOrder(true);
      setError("");
      const orderedIds = crmAccounts.map((account) => account.crmDealerId || account.id);
      await reorderPartnerDealerPriority(orderedIds);
      await loadAccounts();
    } catch (err) {
      setError(err.message || "Unable to save dealer order.");
    } finally {
      setSavingOrder(false);
    }
  };

  const handleOpenDealerPortal = async (dealerId) => {
    try {
      setPortalOpeningId(dealerId);
      setError("");
      const result = await startPartnerDealerPortalSession(dealerId);
      rememberPartnerDealerPortalTarget(dealerId);
      window.location.assign(result.redirectUrl || "/v/automap/dealer/dashboard");
    } catch (err) {
      setError(err.message || "Unable to open dealer portal.");
      setPortalOpeningId("");
    }
  };

  const handlePriorityChange = async (dealerId, value) => {
    const score = Number(value);
    if (Number.isNaN(score)) return;
    try {
      await updatePartnerDealer(dealerId, { searchPriorityScore: score });
      setAccounts((current) =>
        sortAccounts(
          current.map((account) =>
            (account.crmDealerId || account.id) === dealerId
              ? { ...account, searchPriorityScore: score }
              : account
          ),
          sort
        )
      );
    } catch (err) {
      setError(err.message || "Unable to update dealer priority.");
    }
  };

  const handlePriorityInput = (accountId, value) => {
    setAccounts((current) =>
      current.map((account) =>
        account.id === accountId ? { ...account, searchPriorityScore: value } : account
      )
    );
  };

  return (
    <div className="dealer-accounts-hub">
      <header className="dealer-accounts-hub__hero">
        <div>
          <p className="dealer-accounts-hub__eyebrow">{portalLabel}</p>
          <h1>Dealer accounts</h1>
          <p className="dealer-accounts-hub__lede">
            Each card is one dealership business — CRM record, locations, registry profiles, and ingested
            data sources rolled together.
          </p>
        </div>
        <div className="dealer-accounts-hub__hero-actions">
          <button type="button" className="dealer-accounts-hub__ghost-btn" onClick={loadAccounts} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            className="dealer-accounts-hub__primary-btn"
            disabled={savingOrder || crmAccounts.length < 2}
            onClick={handleSaveOrder}
          >
            {savingOrder ? "Saving…" : "Save search order"}
          </button>
        </div>
      </header>

      <section className="dealer-accounts-hub__dashboard" aria-label="Dealer network overview">
        {dashboardStats.map((stat) => (
          <div key={stat.label} className="dealer-accounts-hub__stat">
            <span className="dealer-accounts-hub__stat-value">{stat.value}</span>
            <span className="dealer-accounts-hub__stat-label">{stat.label}</span>
            <span className="dealer-accounts-hub__stat-hint">{stat.hint}</span>
          </div>
        ))}
      </section>

      <section className="dealer-accounts-hub__filters" aria-label="Filter dealer accounts">
        <div className="dealer-accounts-hub__search-wrap">
          <input
            type="search"
            className="dealer-accounts-hub__search"
            placeholder="Search name, city, source, profile…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search dealer accounts"
          />
        </div>

        <div className="dealer-accounts-hub__filter-groups">
          <div className="dealer-accounts-hub__filter-group">
            <span className="dealer-accounts-hub__filter-label">Account</span>
            <div className="dealer-accounts-hub__pills" role="group" aria-label="CRM filter">
              {CRM_FILTERS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`dealer-accounts-hub__pill${crmFilter === option.value ? " is-active" : ""}`}
                  onClick={() => setCrmFilter(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="dealer-accounts-hub__filter-group">
            <span className="dealer-accounts-hub__filter-label">Inventory</span>
            <div className="dealer-accounts-hub__pills" role="group" aria-label="Inventory filter">
              {VEHICLE_FILTERS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`dealer-accounts-hub__pill${vehicleFilter === option.value ? " is-active" : ""}`}
                  onClick={() => setVehicleFilter(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="dealer-accounts-hub__filter-group dealer-accounts-hub__filter-group--sort">
            <label className="dealer-accounts-hub__filter-label" htmlFor="dealer-sort">
              Sort by
            </label>
            <select
              id="dealer-sort"
              className="dealer-accounts-hub__select"
              value={sort}
              onChange={(event) => setSort(event.target.value)}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="dealer-accounts-hub__results-bar">
          <p className="dealer-accounts-hub__results-copy">
            Showing <strong>{filteredAccounts.length}</strong> of {accounts.length} dealer accounts
            {activeFilterCount ? ` · ${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} active` : ""}
          </p>
          {activeFilterCount ? (
            <button type="button" className="dealer-accounts-hub__clear-btn" onClick={clearFilters}>
              Clear filters
            </button>
          ) : null}
        </div>
      </section>

      {error ? <p className="dealer-accounts-hub__error">{error}</p> : null}

      {loading ? (
        <div className="dealer-accounts-hub__loading">
          <div className="dealer-accounts-hub__loading-grid" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="dealer-hub-card dealer-hub-card--skeleton" />
            ))}
          </div>
          <p className="dealer-accounts-hub__loading-copy">Loading dealer accounts…</p>
        </div>
      ) : null}

      {!loading && filteredAccounts.length === 0 && !error ? (
        <div className="dealer-accounts-hub__empty">
          <h2>No dealer accounts match</h2>
          <p className="dealer-accounts-hub__lede">
            Try clearing filters or search for a different dealer, city, or data source.
          </p>
          {activeFilterCount ? (
            <button type="button" className="dealer-accounts-hub__primary-btn" onClick={clearFilters}>
              Clear filters
            </button>
          ) : (
            <p className="dealer-accounts-hub__lede">
              Sign in at <Link to={path("login")}>/v/automap/admin/login</Link> with AutoMapa partner access.
            </p>
          )}
        </div>
      ) : null}

      {!loading && filteredAccounts.length > 0 ? (
        <div className="dealer-accounts-hub__grid">
          {filteredAccounts.map((account) => (
            <DealerAccountCard
              key={account.id}
              account={account}
              path={path}
              portalOpeningId={portalOpeningId}
              onOpenPortal={handleOpenDealerPortal}
              onPriorityChange={handlePriorityChange}
              onPriorityInput={handlePriorityInput}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
